import React, {useCallback, useEffect, useMemo} from 'react';
import {
    cardTypeSet,
    paymentMethodSet,
    paymentTypeSet,
    selectCardType,
    selectDefaultPaymentMethod,
    selectPaymentMethod,
    selectPaymentOptions,
    selectPaymentType,
    selectPreviousPaymentMethod,
} from 'components/customer/Confirmation/store/confirmationSlice';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {shallowEqual} from 'react-redux';
import styled from 'styled-components';
import {PaymentType} from 'components/manufacturer/Payments/entity/Deposit';
import {
    CardType,
    PaymentMethod,
} from 'components/customer/Confirmation/Payment/entity/PaymentSession';
import {PaymentOption} from 'components/customer/Confirmation/Payment/PaymentOption';
import {PaymentMethodType} from 'components/manufacturer/Payments/entity/Setting';
import {cloneDeep} from 'lodash';

export const PaymentOptions = () => {
    const dispatch = useAppDispatch();
    const cardType = useAppSelector(selectCardType);
    const paymentType = useAppSelector(selectPaymentType);
    const selectedPaymentMethod = useAppSelector(selectPaymentMethod);
    const paymentOptions = useAppSelector(selectPaymentOptions, shallowEqual);
    const defaultPaymentMethod = useAppSelector(selectDefaultPaymentMethod);
    const previousPaymentMethod = useAppSelector(selectPreviousPaymentMethod);

    const getGatewayMethods = useCallback(
        () =>
            cloneDeep(
                paymentOptions?.gateway?.paymentMethods?.filter(
                    (method) => method.paymentMethod === cardType
                )
            ),
        [paymentOptions, cardType]
    );

    const getInvoiceMethods = useCallback(
        () => cloneDeep(paymentOptions?.invoice?.paymentMethods),
        [paymentOptions, cardType]
    );

    const getAccountMethods = useCallback(
        () => cloneDeep(paymentOptions?.account?.paymentMethods),
        [paymentOptions, cardType]
    );

    const paymentMethods = useMemo<PaymentMethod[]>(() => {
        if (paymentType && paymentOptions) {
            let options: PaymentMethod[];

            const isAccountPayment =
                previousPaymentMethod === null ||
                previousPaymentMethod === 'account';

            if (isAccountPayment) {
                options = getAccountMethods();
            }

            if (!options && previousPaymentMethod !== 'account') {
                if (paymentType === PaymentType.GATEWAY) {
                    options = getGatewayMethods();
                } else if (paymentType === PaymentType.INVOICE) {
                    options = getInvoiceMethods();
                } else if (paymentType == PaymentType.ACCOUNT) {
                    options = getAccountMethods();
                }
            }

            if (!options) {
                options = getGatewayMethods() || getInvoiceMethods();
            }

            if (options && options.length) {
                const sortKey =
                    defaultPaymentMethod == PaymentMethodType.CARD_FULL ||
                    defaultPaymentMethod == PaymentMethodType.INVOICE_FULL
                        ? 'full'
                        : 'deposit';

                const topOptionIndex = options.findIndex((option) =>
                    option.paymentType.includes(sortKey)
                );

                if (topOptionIndex > -1) {
                    const [topOption] = options.splice(topOptionIndex, 1);

                    options.unshift(topOption);
                }

                return options;
            }
        }

        return [];
    }, [
        paymentOptions,
        paymentType,
        defaultPaymentMethod,
        previousPaymentMethod,
        getGatewayMethods,
        getInvoiceMethods,
        getAccountMethods,
    ]);

    useEffect(() => {
        if (paymentMethods.length && selectedPaymentMethod) {
            const selected = paymentMethods.find(
                (method) => method.paymentType == selectedPaymentMethod
            );

            if (selected == undefined) {
                const paymentMethod = paymentMethods[0].paymentMethod;
                if (paymentMethod == CardType.account) {
                    dispatch(paymentTypeSet(PaymentType.ACCOUNT));
                    dispatch(cardTypeSet(paymentMethod));
                } else if (paymentMethod == CardType.invoice) {
                    dispatch(paymentTypeSet(PaymentType.INVOICE));
                    dispatch(cardTypeSet(paymentMethod));
                } else if (
                    paymentMethod == CardType.card_domestic ||
                    paymentMethod == CardType.card_international
                ) {
                    dispatch(paymentTypeSet(PaymentType.GATEWAY));
                    dispatch(cardTypeSet(paymentMethod));
                }

                dispatch(paymentMethodSet(paymentMethods[0].paymentType));
            }
        }
    }, [selectedPaymentMethod, paymentMethods]);

    return paymentMethods
        ? paymentMethods.map((method, index) => {
              const selected = selectedPaymentMethod == method.paymentType;

              return (
                  <React.Fragment key={method.paymentType}>
                      <PaymentOption
                          selected={selected}
                          method={method}
                          paymentType={paymentType}
                      />
                      {index < paymentMethods.length - 1 ? (
                          <Or>
                              <div>OR</div>
                          </Or>
                      ) : null}
                  </React.Fragment>
              );
          })
        : null;
};

export const Or = styled.div`
    position: relative;
    margin: 15px 125px;
    text-align: center;

    > div {
        display: inline-block;
        padding: 0 10px;
        background: white;
        color: #9b9b9b;
    }

    &:before {
        content: '';
        position: absolute;
        border-bottom: 1px dotted #9b9b9b;
        left: 0;
        right: 0;
        top: 50%;
        z-index: -1;
    }
`;
